import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Phone, ArrowLeft, Key } from 'lucide-react';
import axios from 'axios';

/* =========================
   UTILITÁRIOS DE TELEFONE
========================= */
const onlyNumbers = (value = '') => value.replace(/\D/g, '');

const formatPhoneBR = (value = '') => {
  const numbers = onlyNumbers(value);

  if (numbers.length <= 10) {
    return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  }

  return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
};

const ForgotPassword = () => {
  const { API } = useContext(AuthContext);

  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handlePhoneChange = (e) => {
    setPhone(formatPhoneBR(e.target.value));
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        phone: onlyNumbers(phone)
      });
      
      setResetCodeSent(true);
      toast.success('Código de recuperação enviado!');
      
      // In development, show the code
      if (response.data.reset_code) {
        toast.info(`Código: ${response.data.reset_code}`, { duration: 10000 });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao solicitar recuperação de senha');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Senhas não coincidem');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        phone: onlyNumbers(phone),
        reset_code: resetCode,
        new_password: newPassword
      });
      
      toast.success('Senha alterada com sucesso!');
      // Redirect to login
      window.location.href = '/login';
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 animate-fade-in">
        <div className="text-center">
          <h2 className="text-4xl font-bold text-green-600 mb-2">Recuperar Senha</h2>
          <p className="text-gray-600">
            {resetCodeSent ? 'Digite o código recebido' : 'Digite seu telefone cadastrado'}
          </p>
        </div>

        <Card className="card-feira">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2 text-2xl text-green-600">
              <Key className="w-6 h-6" />
              {resetCodeSent ? 'Alterar Senha' : 'Solicitar Código'}
            </CardTitle>
            <CardDescription>
              {resetCodeSent 
                ? 'Digite o código de 6 dígitos e sua nova senha'
                : 'Enviaremos um código de recuperação para você'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {!resetCodeSent ? (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                    Telefone/WhatsApp
                  </Label>
                  <div className="relative flex items-center">
                    <Phone className="absolute left-3 h-4 w-4 text-gray-500 z-10 pointer-events-none" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="Digite seu telefone"
                      value={phone}
                      onChange={handlePhoneChange}
                      className="pl-10 input-feira"
                      data-testid="forgot-phone-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-feira mt-6"
                  data-testid="request-reset-button"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Enviando...
                    </div>
                  ) : (
                    'Enviar Código'
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="resetCode" className="text-sm font-medium text-gray-700">
                    Código de Recuperação
                  </Label>
                  <Input
                    id="resetCode"
                    name="resetCode"
                    type="text"
                    required
                    placeholder="Digite o código de 6 dígitos"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.toUpperCase())}
                    className="input-feira text-center text-lg tracking-widest"
                    maxLength={6}
                    data-testid="reset-code-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-sm font-medium text-gray-700">
                    Nova Senha
                  </Label>
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    required
                    placeholder="Digite sua nova senha"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-feira"
                    data-testid="new-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                    Confirmar Nova Senha
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    placeholder="Confirme sua nova senha"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-feira"
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-feira mt-6"
                  data-testid="reset-password-button"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Alterando...
                    </div>
                  ) : (
                    'Alterar Senha'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setResetCodeSent(false)}
                  className="w-full mt-2"
                >
                  Voltar
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="text-green-600 hover:text-green-500 font-medium underline flex items-center justify-center gap-2"
                data-testid="back-to-login"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar ao Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
